<template>
  <el-card>
    <el-form
      label-position="top"
      :inline="true"
      size="default"
      :class="{ mobile: !isDesktop }"
      @submit.prevent
    >
      <template v-if="withAdditionals">
        <el-row :gutter="10">
          <el-col :span="6" :xs="24">
            <el-tooltip
              placement="top-start"
              content="Not all metrics are available when you choose all types. When you choose one of the types: in-video, banner, or in-page push, detailed reports will be available."
            >
              <template #default>
                <el-form-item label="Ad type">
                  <AdTypeSelect
                    v-model="filter.ad_types"
                    :with-empty="true"
                    :lables="reportAdTypeLabels"
                  />
                </el-form-item>
              </template>
            </el-tooltip>
          </el-col>
          <el-col :span="6" :xs="24">
            <el-form-item label="Period">
              <DateRangePicker v-model="range" :force-desktop="true" />
            </el-form-item>
          </el-col>
          <el-col :span="6" :xs="12">
            <el-form-item label="Group">
              <GroupBySelect
                :key="aggregatedDates"
                v-model="filter.group_by"
                :aggregation-dates="aggregatedDates"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6" :xs="12">
            <el-form-item>
              <template #label>
                <span>Conversions</span>
                <el-tooltip
                  content="How conversion tracking works"
                  placement="top"
                >
                  <a
                    class="label-help-link"
                    href="https://support.traforama.com/en/articles/conversions-tracking"
                    target="_blank"
                  >
                    <el-icon><QuestionFilled /></el-icon>
                  </a>
                </el-tooltip>
              </template>
              <ConversionTypeSelect
                v-model="filter.conversion_types"
                :multiple="true"
                :stat-conversion-types="statConversionTypes"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </template>
      <template v-else>
        <el-row :gutter="10">
          <el-col :span="6" :xs="24">
            <el-form-item label="Ad type">
              <AdTypeSelect
                v-model="filter.ad_types"
                :lables="reportAdTypeLabels"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </template>
      <el-row :gutter="10">
        <el-col v-if="isRtbUser" :span="12">
          <el-form-item label="Feeds">
            <FeedSelect
              v-model="filter.feeds_ids"
              :items="feeds"
              :multiple="true"
            />
          </el-form-item>
        </el-col>
        <el-col :span="6" :xs="12">
          <el-form-item label="Campaign">
            <CampaignSelect
              v-model="filter.campaigns_ids"
              :items="campaigns"
              :multiple="true"
            />
          </el-form-item>
        </el-col>
        <el-col :span="6" :xs="12">
          <el-form-item label="Creative">
            <CreativeSelect
              v-model="filter.creatures_ids"
              :items="creatives"
              :multiple="true"
            />
          </el-form-item>
        </el-col>
        <el-col :span="6" :xs="12">
          <el-form-item label="Stream">
            <StreamSelect v-model="filter.streams" :multiple="true" />
          </el-form-item>
        </el-col>
        <el-col :span="6" :xs="12">
          <el-form-item label="Country">
            <CountrySelect
              v-model="filter.countries_ids"
              :items="countries"
              :multiple="true"
              :use-id="true"
              style="width: 100%"
            />
          </el-form-item>
        </el-col>
        <el-col v-if="isRtbUser" :span="6" :xs="12">
          <el-form-item label="SubId">
            <SubIdSelect
              v-model="filter.pub_creatures_sub_ids"
              :multiple="true"
              :disabled="aggregatedDates"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="10">
        <el-col :span="6" :xs="12">
          <el-form-item label="Browser">
            <BrowserSelect v-model="filter.browsers" :multiple="true" />
          </el-form-item>
        </el-col>
        <el-col :span="6" :xs="12">
          <el-form-item label="Connection">
            <ConnectionTypeSelect
              v-model="filter.connection_types"
              :multiple="true"
              :disabled="aggregatedDates"
            />
          </el-form-item>
        </el-col>
        <el-col :span="6" :xs="12">
          <el-form-item label="Proxy type">
            <ProxyTypeSelect
              v-model="filter.proxy_types"
              :multiple="true"
              :disabled="aggregatedDates"
            />
          </el-form-item>
        </el-col>
        <el-col :span="6" :xs="12">
          <el-form-item label="Device">
            <DeviceSelect v-model="filter.devices" :multiple="true" />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="10">
        <el-col :span="6" :xs="12">
          <el-form-item label="Platform">
            <PlatformSelect v-model="filter.platforms" :multiple="true" />
          </el-form-item>
        </el-col>
        <el-col :span="6" :xs="12">
          <el-form-item label="Spot">
            <SpotSelect
              v-model="filter.spots_ids"
              :items="spots"
              :multiple="true"
            />
          </el-form-item>
        </el-col>
        <el-col :span="6" :xs="12">
          <el-form-item label="Domain">
            <RefererHostSelect
              v-model="filter.referer_host"
              :multiple="true"
              :disabled="aggregatedDates"
            />
          </el-form-item>
        </el-col>
        <el-col :span="6" :xs="12" class="btn-col">
          <el-form-item>
            <el-button
              type="success"
              icon="SearchElementIcon"
              class="preset-btn"
              round
              size="small"
              @click="refresh"
            >
              Update
            </el-button>
            <el-button
              type="warning"
              icon="RefreshElementIcon"
              class="preset-btn"
              round
              size="small"
              :disabled="!hasAnyFilters"
              @click="reset"
            >
              Reset
            </el-button>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
  </el-card>
</template>

<script lang="ts">
import useResize from '@/hooks/useResize'
import { defineComponent, computed, ref, watch } from 'vue'

import CampaignSelect from '@/components/Select/CampaignSelect.vue'
import CreativeSelect from '@/components/Select/CreativeSelect.vue'
import StreamSelect from '@/components/Select/StreamSelect.vue'
import CountrySelect from '@/components/Select/CountrySelect.vue'
import FeedSelect from '@/components/Select/FeedSelect.vue'
import BrowserSelect from '@/components/Select/BrowserSelect.vue'
import ConnectionTypeSelect from '@/components/Select/ConnectionTypeSelect.vue'
import ProxyTypeSelect from '@/components/Select/ProxyTypeSelect.vue'
import DeviceSelect from '@/components/Select/DeviceSelect.vue'
import PlatformSelect from '@/components/Select/PlatformSelect.vue'
import RefererHostSelect from '@/components/Select/RefererHostSelect.vue'
import SubIdSelect from '@/components/Select/SubIdSelect.vue'
import AdTypeSelect from '@/components/Select/AdTypeSelect.vue'
import SpotSelect from '@/components/Select/SpotSelect.vue'

import GroupBySelect from './GroupBySelect.vue'
import ConversionTypeSelect from './ConversionTypeSelect.vue'
import DateRangePicker from '@/components/DateRangePicker.vue'

import { useCampaignsStore } from '@/apps/traforama/store/campaigns'
import { useFeedsStore } from '@/store/modules/feeds'
import { useCreativesStore } from '@/apps/traforama/store/creatives'
import { useCountriesStore } from '@/store/modules/countries'
import { useSpotsStore } from '@/apps/traforama/store/spots'
import { useUserStore } from '@/store/modules/user'
import { FlatDealAdTypeLabels } from '@/apps/traforama/lables'
import { storeToRefs } from 'pinia'

import { ElMessage } from 'element-plus'
import { QuestionFilled } from '@element-plus/icons-vue'
import {
  DspAdTypeLabels,
  GroupType,
  GroupTypeLabel,
  GroupTypeLabels
} from '@/api/types'
import moment from 'moment'
import useRouter from '@/hooks/useRouter'

export default defineComponent({
  name: 'FilterPanel',
  components: {
    CampaignSelect,
    CreativeSelect,
    StreamSelect,
    CountrySelect,
    BrowserSelect,
    SubIdSelect,
    ConnectionTypeSelect,
    ProxyTypeSelect,
    DeviceSelect,
    PlatformSelect,
    FeedSelect,
    RefererHostSelect,
    AdTypeSelect,
    GroupBySelect,
    ConversionTypeSelect,
    DateRangePicker,
    QuestionFilled,
    SpotSelect
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    withAdditionals: {
      type: Boolean,
      default: false
    },
    skipFilter: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const feedsStore = useFeedsStore()
    const campaignsStore = useCampaignsStore()
    const creativesStore = useCreativesStore()
    const countriesStore = useCountriesStore()
    const spotsStore = useSpotsStore()
    const userStore = useUserStore()

    const { isRtbUser, useConv } = userStore
    const { statConversionTypes } = storeToRefs(userStore)
    const reportAdTypeLabels = computed(() =>
      userStore.hasTabFlatDeal ? FlatDealAdTypeLabels : DspAdTypeLabels
    )
    const { isDesktop } = useResize()
    const logStatsStart = moment().subtract(180, 'days').startOf('day')
    const aggregatedDates = ref(false)

    const filterDefaults: any = {
      campaigns_ids: [],

      creatures_ids: [],

      streams: [],

      countries_ids: [],
      browsers: [],

      connection_types: [],

      proxy_types: [],
      devices: [],
      platforms: [],

      feeds_ids: [],

      referer_host: [],

      pub_creatures_sub_ids: [],

      platforms_ids: [],

      ad_types: null,
      period: '',

      conversion_types: useConv ? [101] : [],

      start_at: moment().subtract(7, 'd').format('YYYY-MM-DD'),

      finish_at: moment().format('YYYY-MM-DD'),

      group_by: GroupType.Date,

      spots_ids: []
    }

    const filter = ref({ ...filterDefaults })
    const { route, router } = useRouter()

    if (route.value.query.campaignId) {
      filter.value.campaigns_ids = [route.value.query.campaignId]
    }

    if (route.value.query.creativeId) {
      filter.value.creatures_ids = [route.value.query.creativeId]
    }

    const reset = () => {
      filter.value = { ...filterDefaults }
    }

    const campaigns = computed(() => {
      if (props.skipFilter) {
        return Object.values(campaignsStore.items)
      }
      return Object.values(campaignsStore.items).filter((c) => {
        return (
          filter.value.ad_types === null ||
          c.attributes.type_id === filter.value.ad_types
        )
      })
    })

    const creatives = computed(() => {
      return creativesStore.currentItems.filter((c) => {
        if (filter.value.campaigns_ids.length > 0) {
          if (
            !filter.value.campaigns_ids.includes(
              String(c.attributes.campaign_id)
            )
          ) {
            return false
          }
        }
        return (
          props.skipFilter ||
          filter.value.ad_types === null ||
          c.attributes.campaign_type_id === filter.value.ad_types
        )
      })
    })

    const countries = computed(() => countriesStore.sorted)
    const feeds = computed(() => Object.values(feedsStore.items))

    const spots = computed(() => {
      return (spotsStore.currentSorted || []).filter((c) => {
        return (
          filter.value.ad_types === null ||
          c.attributes.type_id === filter.value.ad_types ||
          props.skipFilter
        )
      })
    })

    const range = computed({
      get: () => {
        return [filter.value.start_at, filter.value.finish_at]
      },
      set: (val) => {
        if (Array.isArray(val) && val.length === 2) {
          filter.value.start_at = val[0]
          filter.value.finish_at = val[1]
        } else {
          filter.value.start_at = moment(logStatsStart).format('YYYY-MM-DD')
          filter.value.finish_at = moment(new Date()).format('YYYY-MM-DD')
        }

        // update aggregated flag to disable groupings
        aggregatedDates.value = periodIncludesAggregation(filter.value.start_at)
        resetSelectedGrouping()

        if (aggregatedDates.value) {
          resetSelectedFilters()
          showAggregationMessage()
        }
      }
    })

    const showAggregationMessage = () => {
      ElMessage({
        message:
          'Some groups and filters are not available for time frames longer than 180 days.',
        type: 'warning',
        showClose: true,
        duration: 0
      })
    }

    const periodIncludesAggregation = (start: string) => {
      return moment(start, 'YYYY-MM-DD') < logStatsStart
    }

    // reset selected grouping if it's not supported by aggregated data
    const resetSelectedGrouping = () => {
      const group = GroupTypeLabels.find(
        (x: GroupTypeLabel) => x.type_id === filter.value.group_by
      )

      // @ts-ignore: Object is possibly 'undefined'
      if (aggregatedDates.value && !group.aggregation) {
        filter.value.group_by = GroupType.Date
      }
    }

    const resetSelectedFilters = () => {
      filter.value.connection_types = []
      filter.value.proxy_types = []
      filter.value.referer_host = []
      filter.value.pub_creatures_sub_ids = []
    }

    const hasAnyFilters = computed(() => {
      return Object.values(filter.value).some((x: any) => {
        return (Array.isArray(x) && x.length < 1) || !x
      })
    })

    const refresh = () => {
      context.emit('refresh', filter.value)
    }

    const updateFilters = () => {
      if (props.modelValue && props.modelValue.ad_types) {
        filter.value = { ...props.modelValue }
      }
    }

    watch(
      (_: any) => props.modelValue,
      (_) => updateFilters()
    )

    return {
      isDesktop,
      reset,
      hasAnyFilters,
      refresh,
      range,
      campaigns,
      isRtbUser,
      feeds,
      creatives,
      countries,
      spots,
      statConversionTypes,
      filter,
      aggregatedDates,
      reportAdTypeLabels
    }
  }
})
</script>

<style lang="scss" scoped>
.mobile {
  text-align: center;

  .action-buttons {
    float: none;
  }
}

.action-buttons {
  float: right;
}

:deep(.el-form--inline .el-form-item) {
  width: 100%;
}

:deep(.el-select) {
  width: 100%;
}

:deep(.el-form-item--small .el-form-item__label) {
  line-height: 1px;
}

:deep(.preset-btn) {
  margin-top: 20px;
}

.label-help-link {
  margin-left: 4px;
  color: var(--el-text-color-secondary);

  .el-icon {
    vertical-align: text-top;
  }
}

:deep(.el-card__body) {
  padding-bottom: 0;
}

:deep(.el-select) {
  overflow: hidden;
  width: 100% !important;
}

:deep(.el-date-editor--daterange.el-input__inner) {
  width: 100%;
}

@media (max-width: 550px) {
  .btn-col {
    display: flex;
    align-items: center;
    text-align: center;
  }

  :deep(.el-card__body) {
    padding-bottom: 10px;
  }

  :deep(.btn-col .el-button) {
    padding: 7px;
  }
}

@media (max-width: 768px) {
  .el-button + .el-button {
    margin-left: 0;
  }

  :deep(.btn-col .el-form-item) {
    width: 100%;
  }
}
.el-row {
  width: 100%;
}
:deep(.el-date-editor) {
  width: 100%;
}
</style>
